import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { LogoLockup } from '../../components/Logo.tsx';
import { Panel } from '../../components/primitives.tsx';
import { ThemeMenu, ThemeRejectionNotice } from '../../theme/ThemeMenu.tsx';
import { getPortalHelpTopic, PORTAL_HELP_TOPICS } from '../help/topics.ts';

/**
 * The patient portal's own dedicated help page (`BACKLOG.md` W6) — the patient-facing mirror of
 * `web/src/routes/HelpPage.tsx`, rebuilt against this surface's own routing and content module
 * rather than parameterizing that one, matching every other "own module" split already established
 * between the staff and patient surfaces (`docs/decisions/patient-portal-frontend-scope.md`).
 * Sections render `PORTAL_HELP_TOPICS` in their own declared order — that module's own header is
 * explicit this is a real-confusion narrative order, not the feature list, per
 * `claude-kit/rules/invariants.md`.
 *
 * **Public, deliberately not behind `RequirePatientSession`** — same reasoning `HelpPage.tsx` gives
 * for the staff side: no PHI, no API call, static prose describing the portal itself, so a patient
 * can read it (or follow a `PortalHelpLink` from the login or register page) before they have ever
 * signed in — exactly when the "why didn't my code arrive" and "why do I need a password" questions
 * actually come up. `App.tsx` mounts it as a sibling of `/portal/:orgId/login` and
 * `/portal/:orgId/register`, outside `RequirePatientSession`, for the same reason.
 *
 * **Deep-linkable with a graceful stale-link fallback**, identical mechanism to `HelpPage.tsx`:
 * `/portal/:orgId/help/:topicId` scrolls to and focuses that section on mount; `/portal/:orgId/help`
 * alone (or an id that does not match any current topic) falls back to the top of the page rather
 * than a 404 or a silent no-op. Scroll/focus is handled manually in an effect rather than relying on
 * the browser's native anchor-scroll, for the same `<BrowserRouter>`-with-real-paths reason
 * `HelpPage.tsx`'s own header explains: a client-side navigation to a `:topicId` path is a
 * `pushState`, not a full navigation, so it never triggers the browser's own fragment-scroll
 * behaviour.
 *
 * **Its own minimal header, not `PortalShell`'s** — this page sits outside `RequirePatientSession`
 * and is never rendered inside `PortalShell` (`App.tsx`'s routing), so it gets the same bare
 * `LogoLockup` + `ThemeMenu` header row `PortalLoginPage.tsx`/`PortalRegisterPage.tsx` already use
 * for every other public portal page, rather than `HelpPage.tsx`'s own chrome-less staff-side layout
 * — that page has no theme toggle at all because the staff app's own public-page precedent for
 * `/help` predates the pattern this portal's login/register pages already establish, not because a
 * bare layout is this app's actual convention for a standalone public page (`KioskCheckinPage.tsx`
 * confirms the header-row shape is the real, repeated one).
 */
export function PortalHelpPage() {
  const { t } = useTranslation('portalHelp');
  const { topicId } = useParams<{ topicId?: string }>();
  const topRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const target = topicId !== undefined ? document.getElementById(topicId) : null;
    const el = target ?? topRef.current;
    el?.scrollIntoView({ block: 'start' });
    el?.focus();
    // Every section heading and the page's own top heading carry `tabIndex={-1}` below specifically
    // so this focus call has something focusable to land on — a `<section>`/`<h1>` is not
    // focusable by default, and moving focus (not just scrolling) is what actually helps a
    // keyboard or screen-reader user land where the link claimed to send them.
  }, [topicId]);

  const staleLink = topicId !== undefined && getPortalHelpTopic(topicId) === null;

  return (
    <main id="main" className="mx-auto w-full max-w-2xl flex-1 p-4 sm:py-8">
      <div className="mb-6 flex items-center justify-between">
        <LogoLockup size={40} />
        <ThemeMenu />
      </div>
      <ThemeRejectionNotice />

      <div className="flex flex-col gap-6">
        <h1 ref={topRef} tabIndex={-1} className="m-0 text-xl font-semibold text-ink outline-none">
          {t('pageTitle')}
        </h1>

        {staleLink ? (
          <Panel>
            <p className="m-0 text-sm text-ink">{t('staleLink')}</p>
          </Panel>
        ) : null}

        <p className="m-0 max-w-prose text-sm text-ink-muted">{t('intro')}</p>

        {PORTAL_HELP_TOPICS.map((topic) => (
          <section
            key={topic.id}
            id={topic.id}
            tabIndex={-1}
            aria-labelledby={`${topic.id}-heading`}
            className="scroll-mt-4 rounded-panel border border-line bg-surface p-4 shadow-panel outline-none sm:p-6"
          >
            <h2 id={`${topic.id}-heading`} className="mt-0 mb-3 text-lg font-semibold text-ink">
              {t(`topics.${topic.id}.title`, { defaultValue: topic.title })}
            </h2>
            {topic.body.map((paragraph, index) => (
              // Index is a stable key here: `topic.body` is static, declared once in `topics.ts`
              // and never reordered at runtime — the identical reasoning `HelpPage.tsx` gives for
              // its own body-paragraph list.
              <p key={index} className="m-0 mt-2 text-sm text-ink first:mt-0">
                {t(`topics.${topic.id}.body.${String(index)}`, { defaultValue: paragraph })}
              </p>
            ))}
          </section>
        ))}
      </div>
    </main>
  );
}
